import { AnalyticsBrowser } from '@segment/analytics-next';
import { getConsentContext } from './gtm';

// ─── Config ─────────────────────────────────────────────────────────────────
const writeKey = process.env.REACT_APP_SEGMENT_WRITE_KEY;

/**
 * Only send to the production Segment workspace from production builds.
 * Staging / preview / CI must never create MTU in the billed workspace.
 * REACT_APP_SEGMENT_ENABLED lets ops force-disable regardless of env.
 */
const isProdEnv = () => {
    if (process.env.REACT_APP_SEGMENT_ENABLED === 'false') return false;
    if (process.env.REACT_APP_SEGMENT_ENABLED === 'true') return true;
    // Default: only the production environment is allowed to load.
    return process.env.REACT_APP_ENV === 'production'
        || process.env.NODE_ENV === 'production';
};

const isBrowser = () => typeof window !== 'undefined';

// ─── Consent gate ─────────────────────────────────────────────────────────
/**
 * The SDK may load ONLY when the user has explicitly granted analytics consent.
 * "No decision yet" and "rejected" both mean: do not load, do not track.
 *
 * Consent is read from the shared snapshot (getConsentContext): we require
 * analytics_storage === 'granted'. If the snapshot is absent or anything other
 * than granted, this returns false.
 */
const hasAnalyticsConsent = () => {
    const consent = getConsentContext();
    return !!(consent && consent.analytics_storage === 'granted');
};

// ─── Lazy, idempotent SDK loader ────────────────────────────────────────────
let analytics = null;      // the loaded AnalyticsBrowser instance (or null)
let loadAttempted = false;  // guard so we only ever load once per session

/**
 * loadIfConsented — load the Segment SDK exactly once, and only when all gates
 * pass: write key present, production env, and explicit analytics consent.
 * Returns the analytics instance or null. Safe to call repeatedly (idempotent).
 */
const loadIfConsented = () => {
    if (analytics) return analytics;         // already loaded this session
    if (loadAttempted) return null;          // loaded=false but we already tried under current gates
    if (!isBrowser()) return null;
    if (!writeKey) {
        console.warn('[Segment] REACT_APP_SEGMENT_WRITE_KEY not set — tracking disabled.');
        return null;
    }
    if (!isProdEnv()) return null;           // never load from non-prod
    if (!hasAnalyticsConsent()) return null; // no consent → no anonymousId, no MTU

    loadAttempted = true;
    analytics = AnalyticsBrowser.load({ writeKey });
    return analytics;
};

/**
 * refreshAnalyticsConsent — call this from the cookie-consent banner the moment
 * the user ACCEPTS analytics cookies. It attempts the (still gated) load so
 * tracking can begin immediately in the same session without a page reload.
 * If the user has not accepted, this is a no-op.
 */
export const refreshAnalyticsConsent = () => {
    return loadIfConsented();
};

// ─── UTM helpers ─────────────────────────────────────────────────────────
const getStoredUtm = () => {
    if (!isBrowser()) return {};
    return {
        utm_source: localStorage.getItem('utm_source') || null,
        utm_medium: localStorage.getItem('utm_medium') || null,
        utm_campaign: localStorage.getItem('utm_campaign') || null,
        utm_content: localStorage.getItem('utm_content') || null,
    };
};

const getUrlUtm = () => {
    if (!isBrowser()) return {};
    const params = new URLSearchParams(window.location.search);
    return {
        utm_source: params.get('utm_source') || null,
        utm_medium: params.get('utm_medium') || null,
        utm_campaign: params.get('utm_campaign') || null,
        utm_content: params.get('utm_content') || null,
    };
};

// ─── Safe wrappers ───────────────────────────────────────────────────────
// Every wrapper resolves the SDK through loadIfConsented(), so if consent has
// not been granted the call is a silent no-op and NO anonymousId is created.

/**
 * canSend — resolve the SDK for sending. Loads it if needed AND re-checks live
 * consent on every call. If the user revoked analytics mid-session (via the
 * "Cookie settings" panel), the SDK instance stays in memory but this returns
 * null, so no further events are delivered until consent is granted again.
 */
const canSend = () => {
    const a = loadIfConsented();
    if (!a) return null;
    if (!hasAnalyticsConsent()) return null; // consent revoked after load
    return a;
};

const safeTrack = async (event, properties = {}) => {
    const a = canSend();
    if (!a) return;
    try {
        await a.track(event, {
            timestamp: new Date().toISOString(),
            ...properties,
        });
    } catch (err) {
        console.error(`[Segment] track(${event}) failed:`, err.message);
    }
};

const safePage = async (pageName, properties = {}) => {
    const a = canSend();
    if (!a) return;
    try {
        await a.page(pageName, {
            timestamp: new Date().toISOString(),
            ...properties,
        });
    } catch (err) {
        console.error(`[Segment] page(${pageName}) failed:`, err.message);
    }
};

const safeIdentify = async (userId, traits = {}) => {
    const a = canSend();
    if (!a) return;
    try {
        await a.identify(userId, traits);
    } catch (err) {
        console.error(`[Segment] identify(${userId}) failed:`, err.message);
    }
};

// ─── Identity ──────────────────────────────────────────────────────────────
// The server sends userId `tkg_<id>` for identify AND track. The browser MUST
// use the SAME id so the anonymous session stitches onto the one real identity
// instead of being counted as a separate MTU. Keep this in ONE place.
const prefixUserId = (userId) => `tkg_${userId}`;

// ─── Public events ─────────────────────────────────────────────────────────

/**
 * page_viewed — call ONLY for meaningful, intentional views (not auto-fired on
 * every SPA route change). Prefer routing vanity page views through a separate,
 * non-billed analytics tool.
 */
export const trackPageViewed = (pageName) => {
    const utm = { ...getStoredUtm(), ...getUrlUtm() };
    safePage(pageName, {
        page_name: pageName,
        utm_source: utm.utm_source,
        utm_medium: utm.utm_medium,
        utm_campaign: utm.utm_campaign,
        referrer_url: isBrowser() ? document.referrer || null : null,
    });
};

/**
 * signup_started — fire when user begins filling the registration form.
 */
export const trackSignupStarted = (referralCode = null) => {
    const utm = { ...getStoredUtm(), ...getUrlUtm() };
    safeTrack('signup_started', {
        utm_source: utm.utm_source,
        utm_medium: utm.utm_medium,
        utm_campaign: utm.utm_campaign,
        referral_code: referralCode || null,
    });
};

/**
 * identify — call after login to associate the browser session with a user.
 * userId: the backend user ID (raw). Prefixed to `tkg_<id>` to match the server
 * so the same real user is a SINGLE Segment identity (correct MTU + Mailchimp).
 */
export const identifyUser = (userId, traits = {}) => {
    safeIdentify(prefixUserId(userId), traits);
};

export default analytics;
